import React, { useCallback, useEffect, useState } from 'react';
import { ErpService } from '../services/erp.service';
import { EpisodioErp, EpisodiosErp } from '../types/erp.types';

/** Los episodios cambian a lo sumo cada 30 s; no hace falta el ritmo del signo vital. */
const REFRESCO_MS = 60000;
const ZONA = 'America/Mexico_City';

/** «16:18», o «2 oct 16:18» si no es de hoy (hora de México). */
export function horaEpisodio(iso: string, hoy: Date = new Date()): string {
  const d = new Date(iso);
  const dia = (x: Date) => x.toLocaleDateString('es-MX', { timeZone: ZONA });
  const hora = d.toLocaleTimeString('es-MX', {
    timeZone: ZONA,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  if (dia(d) === dia(hoy)) return hora;
  const fecha = d.toLocaleDateString('es-MX', {
    timeZone: ZONA,
    day: 'numeric',
    month: 'short',
  });
  return `${fecha} ${hora}`;
}

/**
 * Duración legible. Un episodio de una sola foto mide 0 entre su primera y su
 * última foto: lo honesto es «≤ 30 s», no «0 s».
 */
export function duracionEpisodio(e: Pick<EpisodioErp, 'duracionSegundos' | 'muestras'>): string {
  if (e.muestras <= 1 || e.duracionSegundos <= 0) return '≤ 30 s';
  const s = e.duracionSegundos;
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m} min ${r} s` : `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

/** Texto corto para la celda; el completo (hasta 600) va en el `title`. */
export function recortar(texto: string | null | undefined, max = 140): string {
  if (!texto) return '(sin texto)';
  return texto.length <= max ? texto : `${texto.slice(0, max - 1)}…`;
}

/**
 * «Trabones recientes» de la pantalla Salud del ERP (2026-10-03).
 *
 * El monitor de arriba es una foto cada 2 minutos y no veía los trabones de
 * 1-3 minutos que dejaban a las tiendas sin modelos. Esta tabla muestra lo que
 * el muestreador de 30 s guardó mientras el ERP estaba degradado: cuándo, cuánto
 * duró, quién (host) y cuál era la consulta más larga.
 */
export const TrabonesRecientes: React.FC = () => {
  const [datos, setDatos] = useState<EpisodiosErp | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      setDatos(await ErpService.getEpisodios());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron leer los trabones');
    }
  }, []);

  useEffect(() => {
    void cargar();
    const t = window.setInterval(() => void cargar(), REFRESCO_MS);
    return () => window.clearInterval(t);
  }, [cargar]);

  const p = datos?.parametros;

  return (
    <section className="serp-trabones">
      <h2>Trabones recientes</h2>
      <p className="serp-nota">
        Últimas 24 horas. Cada 30 s
        {p ? ` (de ${p.horario.desde} a ${p.horario.hasta})` : ''} se mira la
        lista de procesos del ERP y se guarda la foto sólo si estaba degradado:
        {p ? ` ${p.umbralThreadsRunning} o más hilos ejecutando, una consulta de ${p.umbralSegundos} s o más,` : ''}{' '}
        consultas nuestras cortadas por tiempo, o el ERP sin responder. Fotos
        seguidas forman un trabón.
      </p>

      {error && !datos && <div className="serp-vacio">No se pudieron leer los trabones: {error}</div>}

      {datos && datos.episodios.length === 0 && (
        <div className="serp-vacio">Sin trabones en las últimas 24 horas.</div>
      )}

      {datos && datos.episodios.length > 0 && (
        <table className="serp-tabla serp-tabla-trabones">
          <thead>
            <tr>
              <th>Hora</th>
              <th>Duración</th>
              <th className="serp-num">Pico hilos</th>
              <th className="serp-num">Nos cortó</th>
              <th>Quién (host)</th>
              <th>Consulta más larga</th>
            </tr>
          </thead>
          <tbody>
            {datos.episodios.slice(0, 30).map((e) => {
              const larga = e.consultasMasLargas[0];
              const host = e.hosts[0];
              return (
                <tr key={e.inicio}>
                  <td className="serp-base" data-col="Hora">
                    {horaEpisodio(e.inicio)}
                    {e.muestras > 1 && <> – {horaEpisodio(e.fin)}</>}
                  </td>
                  <td data-col="Duración">
                    {duracionEpisodio(e)}
                    {e.sinRespuesta > 0 && (
                      <div className="serp-sin-respuesta">
                        {e.sinRespuesta} sin respuesta
                      </div>
                    )}
                  </td>
                  <td className="serp-num" data-col="Pico hilos">
                    {e.picoThreadsRunning ?? '—'}
                  </td>
                  <td
                    className={`serp-num ${e.nuestrasCortadas > 0 ? 'serp-alerta' : ''}`}
                    data-col="Nos cortó"
                    title={Object.entries(e.cortadasPorFamilia)
                      .map(([f, n]) => `${f}: ${n}`)
                      .join(' · ')}
                  >
                    {e.nuestrasCortadas.toLocaleString('es-MX')}
                  </td>
                  <td className="serp-base" data-col="Quién">
                    {host ? (
                      <span title={e.hosts.map((h) => `${h.host}: ${h.conexiones} conexiones, ${h.activas} activas, ${h.consultas} consultas vistas`).join('\n')}>
                        {host.host}
                        <span className="serp-sub serp-bloque">
                          {host.activas} activas · {host.consultas} consultas
                        </span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="serp-sql" data-col="Consulta más larga">
                    {larga ? (
                      <span title={larga.info ?? ''}>
                        <b className="serp-tiempo">{larga.time} s</b>{' '}
                        {recortar(larga.info)}
                      </span>
                    ) : (
                      <span className="serp-sub">ninguna a la vista</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {datos?.truncado && (
        <p className="serp-nota">Hubo demasiadas fotos: se muestran las más recientes.</p>
      )}
    </section>
  );
};

export default TrabonesRecientes;
